import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const useAuthStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    user: null,
    userProfile: null,
    loading: true,
    error: null,
    
    // Actions
    initializeAuth: () => {
      return new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
            // Get user profile from Firestore
            try {
              const userDoc = await getDoc(doc(db, 'users', user.uid));
              const userProfile = userDoc.exists() ? userDoc.data() : null;
              
              set({ 
                user, 
                userProfile, 
                loading: false, 
                error: null 
              });
            } catch (error) {
              console.error('Error fetching user profile:', error);
              set({ 
                user, 
                userProfile: null, 
                loading: false, 
                error: error.message 
              });
            }
          } else {
            set({ 
              user: null, 
              userProfile: null, 
              loading: false, 
              error: null 
            });
          }
          resolve();
        });
        
        // Store unsubscribe function
        set({ unsubscribeAuth: unsubscribe });
      });
    },

    signup: async (email, password, displayName) => {
      set({ loading: true, error: null });
      try {
        // Create user account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Update display name
        await updateProfile(user, { displayName });

        // Create user document in Firestore
        const userProfile = {
          uid: user.uid,
          email: user.email,
          displayName,
          avatarUrl: null,
          familyId: null,
          role: 'admin',
          createdAt: new Date(),
          updatedAt: new Date(),
          preferences: {
            currency: 'BDT',
            dateFormat: 'DD/MM/YYYY',
            theme: 'light',
            notifications: {
              budgetAlerts: true,
              weeklyReports: true,
              partnerActivity: true
            }
          }
        };

        await setDoc(doc(db, 'users', user.uid), userProfile);
        
        set({ 
          user, 
          userProfile, 
          loading: false, 
          error: null 
        });

        return { success: true, user };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    login: async (email, password) => {
      set({ loading: true, error: null });
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Get user profile
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userProfile = userDoc.exists() ? userDoc.data() : null;

        set({ 
          user, 
          userProfile, 
          loading: false, 
          error: null 
        });

        return { success: true, user };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    logout: async () => {
      set({ loading: true, error: null });
      try {
        await signOut(auth);
        
        // Clean up subscription
        const { unsubscribeAuth } = get();
        if (unsubscribeAuth) {
          unsubscribeAuth();
        }
        
        set({ 
          user: null, 
          userProfile: null, 
          loading: false, 
          error: null,
          unsubscribeAuth: null
        });

        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    resetPassword: async (email) => {
      set({ loading: true, error: null });
      try {
        await sendPasswordResetEmail(auth, email);
        set({ loading: false, error: null });
        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    updateUserProfile: async (updates) => {
      const { user, userProfile } = get();
      if (!user || !userProfile) return { success: false, error: 'No user logged in' };

      set({ loading: true, error: null });
      try {
        // Update Firestore document
        const updatedProfile = {
          ...userProfile,
          ...updates,
          updatedAt: new Date()
        };

        await updateDoc(doc(db, 'users', user.uid), updatedProfile);

        // Update Firebase Auth profile if displayName changed
        if (updates.displayName && updates.displayName !== user.displayName) {
          await updateProfile(user, { displayName: updates.displayName });
        }

        set({ 
          userProfile: updatedProfile, 
          loading: false, 
          error: null 
        });

        return { success: true };
      } catch (error) {
        set({ loading: false, error: error.message });
        return { success: false, error: error.message };
      }
    },

    setFamilyId: async (familyId) => {
      const { user } = get();
      if (!user) return { success: false, error: 'No user logged in' };

      try {
        await updateDoc(doc(db, 'users', user.uid), {
          familyId,
          updatedAt: new Date()
        });

        set(state => ({
          userProfile: {
            ...state.userProfile,
            familyId,
            updatedAt: new Date()
          }
        }));

        return { success: true };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },

    clearError: () => set({ error: null }),

    // Cleanup
    cleanup: () => {
      const { unsubscribeAuth } = get();
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
    }
  }))
);

export default useAuthStore;
